import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { Icd10Selections } from '../../utils/v1/Icd10/Icd10Selections';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { TpoV1RequestFollowingSteps } from './TpoV1RequestFollowingSteps';

interface IInnerTpoV1RequestAddEditState {
    buttonDisabled?: boolean;
}

export function TpoV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    return <InnerTpoV1RequestAddEdit {...props} />;
}

class InnerTpoV1RequestAddEdit extends Component<ICustomControlRequestAddEditProps, IInnerTpoV1RequestAddEditState> {
    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isExaminationRequest
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                {this.addEditForm()}
            </NewOrEditRequestProcessContainer>
        );
    }

    private addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.request}
                />

                <ServiceOrganizationProvider
                    errors={this.props.errors}
                    initialOrganizationId={this.props.request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={this.props.request.selectedProcedureProviderPractitionerId}
                    isExaminationRequest
                    onChange={async (organizationId, practitionerId) => {
                        await this.props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await this.props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await this.props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceProviderServiceId={this.props.service.id}
                />

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await this.props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await this.props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={this.props.request.patientUsesMedication}
                    validateMedications={async () => await this.props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await this.props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangeAllergies={async (allergies) => await this.props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await this.props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await this.props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await this.props.validateOne('requestEpisodesAndJournal')}
                />

                {this.props.service.specialismCode && (
                    <Icd10Selections
                        diagnosis={this.props.request.diagnosis}
                        errors={this.props.errors}
                        onChange={async (diagnosis) => await this.props.handleChange('diagnosis', diagnosis)}
                        onDoValidate={async () => await this.props.validateOne('diagnosis')}
                        specialismCode={this.props.service.specialismCode}
                    />
                )}

                <TpoV1RequestFollowingSteps />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.request}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        );
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await this.props.onSave(requestId);
    }
}
